import React, {useState, useEffect, useCallback} from 'react'
import {Form, Input, Radio, Checkbox} from 'antd'
import {PlusOutlined} from '@ant-design/icons'
import {FLOORS, AREA} from 'lib/enumMapping'
import { EMAIL_REGEX } from 'lib/util'
import Heading from 'components/shared/Heading'
import OrderNavigation from 'components/shared/OrderNavigation'
import Button from 'components/shared/Button'
import BrokerPicker from './BrokerPicker'

/**
 * With antd v4 there is now way to check if the form is valid without doing a
 * validation of the form (`form.validateFields()`). This results in each field
 * showing the "error message". We want to validate the form to check if submit
 * should be disabled or not, but we do not want to show an error message on a
 * field until the user touches it. This function is used to provide props that
 * will override the default behaviour of showing the error for a field.
 */
function hideValidationState(isTouched) {
  if (isTouched) {
    return {}
  }
  return {
    validateStatus: 'success',
    help: ' ', // non empty string ensures the class `ant-form-item-with-help` is added in the dom
  }
}

function EstateInformationForm({handleSubmit, isSubmitting, goToPreviousStep, estate, viewer, contactPerson, possibleContactPersons}) {
  const initialValues = {estateSellers: [{type: 'EstateSeller'}], ...estate.information}
  if (!initialValues.estateSellers.length) {
    initialValues.estateSellers.push({type: 'EstateSeller'})
  }
  const [isDisabled, setDisabled] = useState(false)
  const [form] = Form.useForm()

  function onFieldsChange() {
    const hasErrors = form.getFieldsError().filter(({errors}) => errors.length).length > 0
    setDisabled(hasErrors)
  }

  const validateFields = useCallback(() => {
    form.validateFields().then(() => {
      setDisabled(false)
    })
    .catch(() => {
      setDisabled(true)
    })
  }, [form])

  useEffect(() => {
    validateFields()
  }, [validateFields])

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      scrollToFirstError={true}
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      className="estate-information-form"
    >
      <BrokerPicker
        form={form}
        viewer={viewer}
        contactPerson={contactPerson}
        possibleContactPersons={possibleContactPersons}
      />

      <Form.Item
        noStyle
        shouldUpdate
      >
        {({ isFieldTouched }) => (
          <>

            <Heading level={3}>Kundens referansenummer</Heading>
            <Form.Item name="customerRef">
              <Input
                name="customerRef"
                placeholder="Skriv inn kundens referansenummer. Feks 568977523"
              />
            </Form.Item>

            <Heading level={3}>Antall plan *</Heading>
            <div className="estate-info-field">
              <Form.Item
                {...hideValidationState(isFieldTouched('floors'))}
                name="floors"
                rules={[
                  {required: true, message: 'Antall plan er påkrevd'},
                  {pattern: /[a-d]/, message: 'Antall plan er påkrevd'},
                 ]}
              >
                <Radio.Group name="floors">
                  {Object.entries(FLOORS).map(([value, label]) => (
                    <Radio key={value} value={value}>{label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </div>

            <Heading level={3}>Antall m² *</Heading>
            <div className="estate-info-field">
              <Form.Item
                {...hideValidationState(isFieldTouched('m2'))}
                name="m2"
                rules={[
                  {required: true, message: 'Antall m² er påkrevd'},
                  {pattern: /[a-d]/, message: 'Antall plan er påkrevd'},
                ]}
              >
                <Radio.Group name="m2">
                  {Object.entries(AREA).map(([value, label]) => (
                    <Radio key={value} value={value}>{label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
          </div>

            {estate.segment.type === 'apartment' && (
              <React.Fragment>
                <Heading level={3}>Hvilken etasje er leiligheten i *</Heading>
                <Form.Item
                  {...hideValidationState(isFieldTouched('floorNumber'))}
                  name="floorNumber"
                  rules={[{required: true, message: 'Etasje er påkrevd'}]}
                >
                  <Input
                    name="floorNumber"
                    placeholder="Skriv inn leilighetens etasje. Feks 5"
                  />
                </Form.Item>
              </React.Fragment>
            )}

            <Form.Item name="hasRentalUnit" valuePropName="checked">
              <Checkbox>
                Objektet har utleieleilighet/hybel
              </Checkbox>
            </Form.Item>

            <Form.List name="estateSellers">
              {(fields, {add, remove}) => {
                return (
                  <React.Fragment>
                    {fields.map((field, index) => (
                      <React.Fragment key={field.key}>
                        {index === 0 && (
                          <Heading level={3}>Kontaktperson</Heading>
                        )}
                        {index > 0 && (
                          <div className="contact-person-heading">
                            <Heading level={3}>Alternativ kontaktperson</Heading>
                            <Button
                              size="small"
                              onClick={() => {
                                remove(field.name)
                                setTimeout(validateFields, 10)
                              }}
                            >
                              Fjern
                            </Button>
                          </div>
                        )}
                        <Form.Item
                          {...hideValidationState(isFieldTouched(['estateSellers', field.name, 'name']))}
                          name={[field.name, 'name']}
                          fieldKey={[field.fieldKey, "name"]}
                          label="Navn"
                          rules={[
                            {required: true, message: 'Skriv inn fullt navn'},
                            {pattern: /[^\s|-]+\s+[^\s|-]+/, message: 'Skriv inn fullt navn'},
                          ]}
                        >
                          <Input placeholder="Navn" />
                        </Form.Item>

                        <Form.Item
                          {...hideValidationState(isFieldTouched(['estateSellers', field.name, 'email']))}
                          name={[field.name, 'email']}
                          fieldKey={[field.name, 'email']}
                          label="E-post"
                          rules={[
                            {required: true, message: 'Skriv inn gyldig e-post'},
                            {pattern: EMAIL_REGEX, message: 'Skriv inn gyldig e-post'},
                          ]}
                        >
                          <Input placeholder="E-post" />
                        </Form.Item>

                        <Form.Item
                          {...hideValidationState(isFieldTouched(['estateSellers', field.name, 'phone']))}
                          name={[field.name, 'phone']}
                          fieldKey={[field.name, 'phone']}
                          label="Telefon"
                          rules={[
                            {required: true, message: 'Skriv inn gyldig telefonnummer'},
                            {pattern: /^\+?\d{8,}$/, message: 'Skriv inn gyldig telefonnummer'},
                          ]}
                        >
                          <Input placeholder="Telefon" />
                        </Form.Item>
                      </React.Fragment>
                    ))}
                    <Form.Item>
                      <Button type="dashed" onClick={() => {
                        add({type: 'AdditionalEstateSeller'})
                        setTimeout(validateFields, 10)
                      }}>
                        <PlusOutlined /> Legg til kontaktperson
                      </Button>
                    </Form.Item>
                  </React.Fragment>
                )
              }}
            </Form.List>

            <OrderNavigation
              onBack={goToPreviousStep}
              nextButtonProps={{
                htmlType: 'submit',
                disabled: isSubmitting || isDisabled,
                loading: isSubmitting,
              }}
            />

          </>
        )}
      </Form.Item>
    </Form>
  )
}

export default EstateInformationForm
